/*
 * @Description: 给 @tencent/search-uigs 的上报入口（UIGS.prototype.send）打补丁，
 *   统一由七彩石 switch_config 的两个开关控制上报：
 *     - sendUigsLog: 是否执行原始 UIGS 上报
 *     - sendAttaLog: 是否镜像一份 ATTA 上报（与 video-pc 共用同一张 ATTA 表）
 *   七彩石侧原始配置值为 'on'/'off'，由 entry-client 从 store.$config 读取后统一转换为
 *   boolean 再传给 patchUigsAtta()，仅需在客户端入口调用一次。
 *   注意：本文件为纯 ESM，不 import 任何 CommonJS 模块，避免被打进入口图时触发
 *   "ES Modules may not assign module.exports" 运行时错误。
 */
import UIGS from '@tencent/search-uigs';

// ATTA（2.0）上报配置
const ATTA_REPORT_URL = 'https://trace.inlong.qq.com/atta2nd/dataproxy/message';

const ATTA_ENDPOINT_MAP = {
  wap_video: { groupId: 'gwe000355064e5', streamId: 'swe000046930b3' },
  wap_video_kd: { groupId: 'gwe00035507147', streamId: 'swe00004693783' },
};
/**
 * 按 uigs_productid 获取对应的 ATTA 接入点配置
 * @param {String} productId 来自 UIGS 实例构造参数 uigs_productid
 * @return {{groupId: String, streamId: String}}
 */
function getAttaEndpoint(productId = 'wap_video') {
  return ATTA_ENDPOINT_MAP[productId];
}

// ATTA 镜像上报时需要过滤的 UIGS 内部字段（新增过滤字段直接往此 Set 追加即可）
const ATTA_EXCLUDE_FIELDS = new Set(['_origin_', '_suffix_']);

/**
 * 将参数对象序列化为 application/x-www-form-urlencoded 字符串
 * @description 对象值先 JSON.stringify，所有值统一 encodeURIComponent，
 *   避免含特殊字符的值（如 uigs_refer=document.referrer）破坏 formData 结构导致 ATTA 错列
 * @param {Object} obj
 * @return {String} key=value&key=value...
 */
function paramsFormat(obj) {
  return Object.keys(obj || {})
    .map((key) => {
      let val = obj[key];
      if (val === undefined || val === null) {
        val = '';
      } else if (typeof val === 'object') {
        val = JSON.stringify(val);
      }
      return `${key}=${window.encodeURIComponent(val)}`;
    })
    .join('&');
}

/**
 * 读取 cookie（本文件为纯 ESM，不引入 CommonJS 版 CookieGet，故本地实现一个轻量版）
 * @description 若目标 cookie 带 HttpOnly 属性，浏览器会禁止 document.cookie 读取到它（防 XSS 窃取），
 *   此时本方法只能返回空字符串，需改为服务端渲染时写入 window 全局变量再由前端读取
 * @param {String} name cookie key，仅供内部固定字段读取，非用户可控输入
 * @return {String}
 */
function getCookie(name) {
  const { cookie } = document;
  if (!cookie || !name) return '';
  const start = cookie.indexOf(`${name}=`);
  if (start === -1) return '';
  const valueStart = start + name.length + 1;
  const end = cookie.indexOf(';', valueStart);
  const raw = cookie.substring(valueStart, end === -1 ? cookie.length : end);
  try {
    return window.decodeURIComponent(raw);
  } catch (e) {
    return raw;
  }
}

/**
 * ATTA 镜像上报需要额外补充的固定字段（每次上报时取最新值，非静态常量）
 * @return {Object}
 */
function getAttaExtraFields() {
  return {
    // 用户ip，由服务端渲染写入 window.clientIp
    logip: (window.clientIp || '').toString(),
    // 发送日志请求的时间戳
    manualtime: Date.now(),
    // 当前页面url
    pageurl: window.location.href,
    // 用户ua
    useragent: navigator.userAgent || '',
    // suv取自cookie
    suv: getCookie('SUV') || '',
  };
}

/**
 * ATTA 上报
 * @description POST 到 ATTA 2.0（inlong dataproxy）接入点；业务参数先拼成 body（k=v&k=v...），
 *   再和 groupId/streamId 一起作为外层表单整体 encode 后发出
 * @param {Object} params 业务上报参数（与 UIGS 上报参数一致，含真实 pbtype、uigs_productid）
 * @注意 pbtype/logip/manualtime/pageurl/useragent/suv 均为默认值，
 *   若 params 中带有真实同名字段（如真实 pbtype）则以 params 为准；
 *   groupId/streamId 按 params.uigs_productid 路由到对应 ATTA 接入点
 */
function reportAtta(params = {}) {
  const bizData = Object.assign(
    {
      pbtype: 'pv',
    },
    getAttaExtraFields(),
    params,
  );
  const { groupId, streamId } = getAttaEndpoint(bizData.uigs_productid);
  const body = paramsFormat(bizData);
  const data = {
    groupId,
    streamId,
    body,
    cnt: 1,
  };
  const formData = Object.keys(data)
    .map(key => `${key}=${window.encodeURIComponent(data[key])}`)
    .join('&');
  try {
    if (typeof window.fetch === 'function') {
      window
        .fetch(ATTA_REPORT_URL, {
          method: 'POST',
          body: formData,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
          keepalive: true,
        })
        .catch(() => {
          // 忽略上报失败，避免影响业务主流程
        });
    } else {
      // 降级：无 fetch 时用 GET 像素上报
      const img = new Image();
      img.src = `${ATTA_REPORT_URL}?${formData}`;
    }
  } catch (e) {
    // 静默失败，不影响业务
  }
}

let patched = false;

/**
 * 给 UIGS.prototype.send 打补丁，由七彩石开关统一控制 UIGS / ATTA 上报
 * @description UIGS 是模块单例，patch 一次即对所有页面/实例生效
 * @param {Function|Object} getFlags 开关来源，可传对象或返回对象的函数
 *   （传函数可在每次上报时读取最新的七彩石配置），形如
 *   { sendUigsLog: boolean, sendAttaLog: boolean }
 *   缺省：sendUigsLog 默认 true（保持既有 UIGS 上报），sendAttaLog 默认 false（不额外上报）
 */
export function patchUigsAtta(getFlags) {
  if (patched || !UIGS || !UIGS.prototype || typeof UIGS.prototype.send !== 'function') {
    return;
  }
  patched = true;

  const originalSend = UIGS.prototype.send;
  UIGS.prototype.send = function send(options, sampling) {
    let sendUigsLog = true;
    let sendAttaLog = false;
    try {
      const flags = (typeof getFlags === 'function' ? getFlags() : getFlags) || {};
      sendUigsLog = flags.sendUigsLog !== false;
      sendAttaLog = flags.sendAttaLog === true;
    } catch (e) {
      // 读取开关失败时退化为默认：仅保留 UIGS 上报
      sendUigsLog = true;
      sendAttaLog = false;
    }
    // UIGS 原始上报：由 sendUigsLog 控制
    let result;
    if (sendUigsLog) {
      result = originalSend.call(this, options, sampling);
    }
    // ATTA 镜像上报：由 sendAttaLog 控制
    try {
      if (sendAttaLog) {
        const merged = Object.assign({}, this.opts, options || {});
        const params = {};
        Object.keys(merged).forEach((key) => {
          if (!ATTA_EXCLUDE_FIELDS.has(key)) {
            params[key] = merged[key];
          }
        });
        reportAtta(params);
      }
    } catch (e) {
      // 静默失败，绝不影响主上报流程
    }
    return result;
  };
}

export default patchUigsAtta;
